import { computed, onMounted, ref } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import incomeService, { type Income } from "@/services/income";

export function useIncomeList() {
  const { t } = useI18n();
  const incomes = ref<Income[]>([]);
  const loading = ref(false);
  const searchQuery = ref("");

  const fetchIncomes = async () => {
    loading.value = true;
    try {
      incomes.value = await incomeService.getIncomes();
    } catch (error) {
      console.error("Error fetching incomes:", error);
      Swal.fire({ icon: "error", title: t("income_page.load_failed"), text: t("income_page.unable_to_load_incomes") });
    } finally {
      loading.value = false;
    }
  };

  const filteredIncomes = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return incomes.value;
    return incomes.value.filter((income: any) =>
      [
        income.ref,
        income.description,
        income.category?.name || income.category_name,
        income.warehouse?.name || income.warehouse_name,
        income.warehouse?.code || income.warehouse_code,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  });

  const deleteIncome = async (income: Income) => {
    if (!income.id) return;
    const result = await Swal.fire({
      title: t("income_page.delete_income_title"),
      text: t("income_page.delete_income_text", { ref: income.ref }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("income_page.confirm_delete"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;

    try {
      await incomeService.deleteIncome(income.id);
      await fetchIncomes();
      Swal.fire({ icon: "success", title: t("income_page.deleted"), text: t("income_page.income_deleted_successfully"), timer: 1400, showConfirmButton: false });
    } catch (error) {
      console.error("Error deleting income:", error);
      Swal.fire({ icon: "error", title: t("income_page.delete_failed"), text: t("income_page.unable_to_delete_income") });
    }
  };

  onMounted(fetchIncomes);

  return {
    searchQuery,
    loading,
    filteredIncomes,
    deleteIncome,
  };
}
